import React, { useEffect, useMemo, useState } from 'react'

import Box from '@mui/material/Box'
import {
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import ViewKanban from '@mui/icons-material/ViewKanban'
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline'
import TabPanel from './TabPanel'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import ListTab from './ListTab'
import KanbanTab from './KanbanTab'

const MixedViews = ({
  items,
  defaultTab,
  columns,
  onItemClick,
  selectedStatuses,
  setSelectedStatuses,
  statusOptions = ['New', 'In Progress', 'Waiting', 'Completed'],
}) => {
  const [selectedTab, setSelectedTab] = useState(defaultTab ?? 1)
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState(null)
  const [typeMenuAnchorEl, setTypeMenuAnchorEl] = useState(null)
  const [selectedTypes, setSelectedTypes] = useState([])

  const typeOptions = useMemo(
    () =>
      items.reduce((accumulator, item) => {
        if (!accumulator.includes(item.type)) {
          accumulator.push(item.type)
        }
        return accumulator
      }, []),
    [items]
  )

  const searchResults = useMemo(
    () =>
      items.filter(
        (x) =>
          selectedStatuses.includes(x.status) && selectedTypes.includes(x.type)
      ),
    [items, selectedStatuses, selectedTypes]
  )

  useEffect(() => {
    setSelectedTypes(typeOptions) // always reset types when items change
  }, [typeOptions])

  return (
    <Stack
      component={Paper}
      elevation={0}
      padding={1.5}
      gap={1}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', lg: 'center' }}
        gap={1}
      >
        <Box>
          <Tabs
            value={selectedTab}
            onChange={(event, newValue) => setSelectedTab(newValue)}
          >
            <Tab
              label="List"
              icon={<ViewTimelineIcon />}
              iconPosition="start"
              tabIndex={0}
              columns={columns}
            />
            <Tab
              label="Kanban"
              icon={<ViewKanban />}
              iconPosition="start"
              tabIndex={1}
            />
          </Tabs>
        </Box>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          gap={1}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Stack
            direction="row"
            gap={0.5}
            alignItems="center"
            justifyContent={{ xs: 'space-between', md: 'flex-start' }}
          >
            <Typography variant="body2">Status</Typography>
            <Typography variant="body2" fontWeight="bold">
              {selectedStatuses.length === statusOptions.length
                ? 'All'
                : selectedStatuses.length > 0
                ? selectedStatuses.join(', ')
                : 'None'}
            </Typography>
            <ArrowDropDownIcon
              sx={{ cursor: 'pointer' }}
              onClick={(e) => setStatusMenuAnchorEl(e.currentTarget)}
            />
            <Menu
              open={!!statusMenuAnchorEl}
              anchorEl={statusMenuAnchorEl}
              onClose={() => setStatusMenuAnchorEl(null)}
            >
              {statusOptions.map((status) => (
                <MenuItem
                  key={status}
                  onClick={() => {
                    setSelectedStatuses((prev) =>
                      prev.includes(status)
                        ? prev.filter((x) => x !== status)
                        : [...prev, status]
                    )
                    setStatusMenuAnchorEl(null)
                  }}
                >
                  <Stack direction="row" gap={1}>
                    <div>
                      {selectedStatuses.includes(status) ? (
                        <CheckBoxIcon />
                      ) : (
                        <CheckBoxOutlineBlankIcon />
                      )}
                    </div>
                    <div>{status}</div>
                  </Stack>
                </MenuItem>
              ))}
            </Menu>
          </Stack>
          <Stack
            direction="row"
            gap={0.5}
            alignItems="center"
            justifyContent={{ xs: 'space-between', md: 'flex-start' }}
          >
            <Typography variant="body2">Type</Typography>
            <Typography variant="body2" fontWeight="bold">
              {selectedTypes.length === typeOptions.length
                ? 'All'
                : selectedTypes.length > 0
                ? selectedTypes.join(', ')
                : 'None'}
            </Typography>
            <ArrowDropDownIcon
              sx={{ cursor: 'pointer' }}
              onClick={(e) => setTypeMenuAnchorEl(e.currentTarget)}
            />
            <Menu
              open={!!typeMenuAnchorEl}
              anchorEl={typeMenuAnchorEl}
              onClose={() => setTypeMenuAnchorEl(null)}
            >
              {typeOptions.map((type) => (
                <MenuItem
                  key={type}
                  onClick={() =>
                    setSelectedTypes((prev) =>
                      prev.includes(type)
                        ? prev.filter((x) => x !== type)
                        : [...prev, type]
                    )
                  }
                >
                  <Stack direction="row" gap={1}>
                    <div>
                      {selectedTypes.includes(type) ? (
                        <CheckBoxIcon />
                      ) : (
                        <CheckBoxOutlineBlankIcon />
                      )}
                    </div>
                    <div>{type}</div>
                  </Stack>
                </MenuItem>
              ))}
            </Menu>
          </Stack>
        </Stack>
      </Stack>
      <TabPanel value={selectedTab} index={1}>
        <KanbanTab items={searchResults} selectedStatuses={selectedStatuses} />
      </TabPanel>
      <TabPanel value={selectedTab} index={0}>
        <ListTab
          rows={searchResults}
          columns={columns}
          onRowClick={onItemClick}
          selectedStatuses={selectedStatuses}
        />
      </TabPanel>
    </Stack>
  )
}

export default MixedViews
